import { useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { ROOM } from "../../lib/layout";
import { day, updateDay } from "../../lib/daylight";

// High openings: above the Feeling Wall between the arches, and along both side walls (dawn and sunset rays).
const ZB = ROOM.D / 2;
const XB = ROOM.W / 2;
const Y0 = 3.95;
const Y1 = 4.6;
const YC = (Y0 + Y1) / 2;
const OW = 1.2;
const OPEN = [
  ...[-10, -6, 6, 10].map((x) => ({ c: [x, YC, -ZB + 0.02], n: [0, 0, 1], t: [1, 0, 0], rot: 0 })),
  ...[-4, 0, 4].map((z) => ({ c: [XB - 0.02, YC, z], n: [-1, 0, 0], t: [0, 0, 1], rot: -Math.PI / 2 })),
  ...[-4, 0, 4].map((z) => ({ c: [-XB + 0.02, YC, z], n: [1, 0, 0], t: [0, 0, 1], rot: Math.PI / 2 })),
  ...[-4, 0, 4].map((x) => ({ c: [x, ROOM.H - 0.02, -4.2], n: [0, -1, 0], t: [1, 0, 0], rot: 0, sky: true })),
].map((o) => ({ ...o, c: new THREE.Vector3(...o.c), n: new THREE.Vector3(...o.n), t: new THREE.Vector3(...o.t) }));

const vert = `varying vec2 vUv; varying vec3 vW;
void main(){ vUv = uv; vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`;

const rayFrag = `uniform vec3 uRayCol; uniform float uRayK, uTime, uMotion, uSeed, uW, uD, uOn;
varying vec2 vUv; varying vec3 vW;
void main(){
  if (uOn < 0.01 || abs(vW.x) > uW * 0.5 || abs(vW.z) > uD * 0.5 || vW.y < 0.0) discard;
  float edge = smoothstep(0.0, 0.34, vUv.x) * smoothstep(1.0, 0.66, vUv.x);
  float along = pow(vUv.y, 0.8) * smoothstep(0.0, 0.25, vUv.y);
  float tm = uTime * uMotion;
  float streak = 0.55 + 0.45 * sin(vUv.x * 19.0 + uSeed * 3.0 + sin(vUv.y * 3.0 + tm * 0.21) * 1.5 + tm * 0.13);
  streak *= 0.75 + 0.25 * sin(vUv.x * 7.0 - tm * 0.09 + uSeed);
  float breathe = 0.8 + 0.2 * sin(tm * 0.17 + uSeed * 2.1);
  float near = smoothstep(0.8, 3.2, distance(cameraPosition, vW));
  gl_FragColor = vec4(uRayCol * vec3(1.0, 0.9, 0.72) * edge * along * streak * breathe * near * uRayK * uOn * 0.27, 1.0);
}`;

const skyFrag = `uniform vec3 uSkyTop, uSkyBot; uniform float uLamp; varying vec2 vUv;
void main(){
  vec3 c = mix(uSkyBot, uSkyTop, vUv.y);
  float frame = step(0.06, vUv.x) * step(vUv.x, 0.94) * step(0.1, vUv.y) * step(vUv.y, 0.9);
  float mull = step(0.018, abs(vUv.x - 0.5));
  vec3 wood = mix(vec3(0.93, 0.89, 0.82), vec3(1.0, 0.82, 0.55), uLamp * 0.5);
  gl_FragColor = vec4(mix(wood, c, frame * mull), 1.0);
  #include <colorspace_fragment>
}`;

const moteVert = `attribute float aAng; attribute float aA; uniform float uPx; varying float vAng; varying float vA;
void main(){ vAng = aAng; vA = aA; vec4 mv = modelViewMatrix * vec4(position, 1.0); gl_PointSize = uPx / -mv.z; gl_Position = projectionMatrix * mv; }`;
// streak-shaped specks, never round
const moteFrag = `uniform vec3 uRayCol; uniform float uRayK; varying float vAng; varying float vA;
void main(){
  vec2 c = gl_PointCoord - 0.5;
  float ca = cos(vAng), sa = sin(vAng);
  vec2 r = vec2(ca * c.x - sa * c.y, sa * c.x + ca * c.y);
  float a = smoothstep(0.06, 0.0, abs(r.y)) * smoothstep(0.5, 0.12, abs(r.x)) * vA * uRayK;
  gl_FragColor = vec4(uRayCol * a * 0.6, 1.0);
}`;

const D = new THREE.Vector3();
// how far light entering at c travels before leaving the hall box
function travel(c, d) {
  let t = c.y / Math.max(-d.y, 0.05);
  if (d.x > 0.01) t = Math.min(t, (XB - c.x) / d.x);
  if (d.x < -0.01) t = Math.min(t, (-XB - c.x) / d.x);
  if (d.z > 0.01) t = Math.min(t, (ZB - c.z) / d.z);
  if (d.z < -0.01) t = Math.min(t, (-ZB - c.z) / d.z);
  return Math.min(Math.max(t, 0.1), 22);
}
// 0..1: how squarely sunlight enters through this opening
const facing = (o, S) => THREE.MathUtils.smoothstep(-o.n.dot(S), 0.05, 0.3);

function Shafts({ uTime }) {
  const sheets = useMemo(
    () =>
      OPEN.flatMap((o, i) =>
        [0, 1].map((k) => ({
          o,
          k,
          mat: new THREE.ShaderMaterial({ vertexShader: vert, fragmentShader: rayFrag, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, toneMapped: false, uniforms: { ...day, uTime, uSeed: { value: i * 1.37 + k * 0.5 }, uW: { value: ROOM.W }, uD: { value: ROOM.D }, uOn: { value: 0 } } }),
        })),
      ),
    [uTime],
  );
  const geo = useMemo(() => new THREE.PlaneGeometry(1, 1), []);
  const refs = useMemo(() => [], []);
  const m4 = useMemo(() => new THREE.Matrix4(), []);
  useFrame(() => {
    const S = day.uSunDir.value;
    D.copy(S).negate();
    sheets.forEach((s, i) => {
      const m = refs[i];
      if (!m) return;
      const on = facing(s.o, S);
      s.mat.uniforms.uOn.value = on;
      m.visible = on > 0.01;
      if (!m.visible) return;
      const L = travel(s.o.c, D);
      const a = s.o.t.clone().addScaledVector(S, -s.o.t.dot(S)).normalize();
      const across = s.k ? a.clone().cross(S).normalize() : a;
      const normal = across.clone().cross(S).normalize();
      m4.makeBasis(across.multiplyScalar(s.k ? OW * 0.55 : OW * 0.95), S.clone().multiplyScalar(L), normal);
      m4.setPosition(s.o.c.x + D.x * L * 0.5, s.o.c.y + D.y * L * 0.5, s.o.c.z + D.z * L * 0.5);
      m.matrix.copy(m4);
      m.matrixWorldNeedsUpdate = true;
    });
  });
  return sheets.map((s, i) => <mesh key={i} ref={(m) => (refs[i] = m)} geometry={geo} material={s.mat} matrixAutoUpdate={false} frustumCulled={false} renderOrder={5} />);
}

const PER = 18;
function Motes({ still }) {
  const { geo, mat, seeds } = useMemo(() => {
    const n = OPEN.length * PER;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    const ang = new Float32Array(n);
    const al = new Float32Array(n);
    const seeds = [];
    for (let i = 0; i < n; i++) {
      ang[i] = Math.random() * Math.PI;
      al[i] = 0.35 + Math.random() * 0.65;
      seeds.push({ u: Math.random() - 0.5, v: 0.06 + Math.random() * 0.8, p: Math.random() * 6.28, s: 0.004 + Math.random() * 0.01 });
    }
    g.setAttribute("aAng", new THREE.BufferAttribute(ang, 1));
    g.setAttribute("aA", new THREE.BufferAttribute(al, 1));
    const m = new THREE.ShaderMaterial({ vertexShader: moteVert, fragmentShader: moteFrag, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, uniforms: { ...day, uPx: { value: 60 } } });
    return { geo: g, mat: m, seeds };
  }, []);
  useFrame(() => {
    const t = performance.now() / 1000;
    const S = day.uSunDir.value;
    D.copy(S).negate();
    const pos = geo.attributes.position;
    OPEN.forEach((o, j) => {
      const on = facing(o, S) > 0.2;
      const L = travel(o.c, D);
      for (let q = 0; q < PER; q++) {
        const i = j * PER + q;
        const d = seeds[i];
        if (!on) {
          pos.setXYZ(i, 0, -50, 0);
          continue;
        }
        const v = (still ? d.v : (d.v + t * d.s) % 0.86) * L;
        const wob = still ? 0 : Math.sin(t * 0.3 + d.p) * 0.08;
        const u = d.u * OW * 0.8 + wob;
        pos.setXYZ(i, o.c.x + o.t.x * u + D.x * v, o.c.y + D.y * v + (still ? 0 : Math.sin(t * 0.2 + d.p) * 0.05), o.c.z + o.t.z * u + D.z * v);
      }
    });
    pos.needsUpdate = true;
    if (!still) {
      const a = geo.attributes.aAng.array;
      for (let i = 0; i < a.length; i++) a[i] += 0.0015 * Math.sin(i);
      geo.attributes.aAng.needsUpdate = true;
    }
  });
  return <points geometry={geo} material={mat} frustumCulled={false} renderOrder={6} />;
}

function Openings() {
  const mat = useMemo(() => new THREE.ShaderMaterial({ vertexShader: vert, fragmentShader: skyFrag, uniforms: day }), []);
  return OPEN.map((o, i) => (
    <mesh key={i} position={o.c} rotation={o.sky ? [Math.PI / 2, 0, 0] : [0, o.rot, 0]} material={mat}>
      <planeGeometry args={o.sky ? [OW + 0.12, OW + 0.12] : [OW + 0.12, Y1 - Y0 + 0.14]} />
    </mesh>
  ));
}

export function Daylight({ u, still }) {
  useFrame((_, dt) => updateDay(dt, still));
  return (
    <group>
      <Openings />
      <Shafts uTime={u.uTime} />
      <Motes still={still} />
    </group>
  );
}
